import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { Search, Plus, Shield, Sparkles, Users, Trophy, X } from "lucide-react";
import { toast } from "sonner";

import ClanLogo from "../components/clan/ClanLogo";

export default function ClansPage() {
  const { user } = useAuth();
  const [clans, setClans] = useState([]);
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", tag: "", description: "" });
  const [loadingCreate, setLoadingCreate] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/clans", { params: { q } });
      setClans(data);
    } catch (err) {
      console.error("فشل جلب الكلانات", err);
    }
  }, [q]);

  useEffect(() => {
    const timer = setTimeout(() => {
      load();
    }, 300); // Debounce لتقليل الطلبات
    return () => clearTimeout(timer);
  }, [q, load]);

  const create = async (e) => {
    e.preventDefault();
    if (loadingCreate) return;
    setLoadingCreate(true);
    try {
      const { data } = await api.post("/clans", form);
      toast.success("تم إنشاء الكلان بنجاح! 🎉");
      setShowCreate(false);
      setForm({ name: "", tag: "", description: "" });
      await load();
      window.location.href = `/clans/${data.id}`;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoadingCreate(false);
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      {/* الهيدر العلوي */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-white">الكلانات</h1>
          <p className="text-white/50 mt-1 text-sm">اكتشف الفرق المنافسة، انضم لأقوى الكلانات أو أنشئ كلانك الخاص</p>
        </div>
        {user?.clan_id && (
          <Link to={`/clans/${user.clan_id}`} data-testid="my-clan-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 transition text-sm">
            <Shield size={18} /> كلاني
          </Link>
        )}
        {user && !user.clan_id && (
          <button
            data-testid="create-clan-btn"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 shadow-[0_0_20px_rgba(255,204,0,0.3)] hover:shadow-[0_0_25px_rgba(255,204,0,0.5)] hover:scale-105 transition-all duration-200 text-sm"
          >
            <Plus size={18} /> إنشاء كلان جديد
          </button>
        )}
      </div>

      {/* حقل البحث */}
      <div className="relative max-w-xl">
        <Search size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          data-testid="search-clans"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث باسم الكلان أو التاج..."
          className="w-full bg-[#111113]/80 border border-white/10 rounded-xl pr-11 pl-4 py-3 outline-none focus:border-gold-500/50 text-sm text-white placeholder:text-white/30 transition shadow-inner"
        />
      </div>

{/* شبكة عرض كروت الكلانات */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {clans.map((c) => {
          const logoSrc = c.logo_url || c.avatar || c.logo;
          const membersCount = Array.isArray(c.member_ids)
            ? c.member_ids.length
            : (Array.isArray(c.members) ? c.members.length : 0);
          const isClanPlus = !!(c.is_plus || c.isPlusClan || c.plan === "plus");

          // 🎯 استخراج بيانات الرانك لعرضها في الكرت
          const rankTier = c.rank_tier || "Unranked";
          const matchesPlayed = c.matches_played_this_season || 0;
          
          const tierBadges = {
            "Elite": { text: "Elite", color: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" },
            "Pro": { text: "Pro", color: "bg-gold-500/10 border-gold-500/30 text-gold-400" },
            "Rookie": { text: "Rookie", color: "bg-white/5 border-white/10 text-white/60" },
            "Unranked": { text: `تقييم ${matchesPlayed}/5`, color: "bg-purple-500/10 border-purple-500/30 text-purple-300" }
          };
          const currentBadge = tierBadges[rankTier] || tierBadges["Unranked"];

          return (
            <Link
              key={c.id}
              to={`/clans/${c.id}`}
              data-testid={`clan-card-${c.id}`}
              className={`relative bg-[#111113]/90 border rounded-2xl p-5 flex flex-col justify-between gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                isClanPlus
                  ? "border-gold-500/40 bg-gradient-to-b from-gold-500/5 via-transparent to-transparent shadow-[0_0_15px_rgba(255,204,0,0.05)]"
                  : "border-white/10 hover:border-gold-500/30"
              }`}
            >
              <div className="flex items-start gap-3.5">
                <ClanLogo
                  clan={{ logo: logoSrc }}
                  className="h-14 w-14 rounded-xl bg-gold-500/10 border border-gold-500/30 grid place-items-center text-gold-500 overflow-hidden shrink-0"
                  fallbackIconSize={24}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-display font-black text-lg truncate text-white flex items-center gap-1.5">
                    <span className="truncate">{c.name}</span>
                    {isClanPlus && (
                      <Sparkles size={13} className="text-[#d4af37] shrink-0 drop-shadow-[0_0_8px_rgba(212,175,55,0.7)]" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-xs font-mono font-bold text-gold-500 bg-gold-500/10 px-2 py-0.5 rounded-md border border-gold-500/20">
                      [{c.tag}]
                    </span>
                    {/* شارة الرانك الصغيرة في الكرت */}
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${currentBadge.color}`}>
                      {currentBadge.text}
                    </span>
                  </div>
                </div>
              </div>

              {c.description ? (
                <p className="text-xs text-white/60 line-clamp-2 leading-relaxed h-8">{c.description}</p>
              ) : (
                <p className="text-xs text-white/30 italic h-8">— لا يوجد وصف —</p>
              )}

              <div className="pt-3 border-t border-white/5 flex items-center justify-between text-xs text-white/50">
                <div className="flex items-center gap-1.5 font-bold">
                  <Users size={14} className="text-white/40" />
                  <span>{membersCount} أعضاء</span>
                </div>
                <div className="flex items-center gap-1.5 font-display font-black text-gold-500">
                  <Trophy size={14} className="text-gold-500" />
                  <span>{c.points || 0} pts</span>
                </div>
              </div>
            </Link>
          );
        })}
        {clans.length === 0 && (
          <div className="col-span-full text-center text-white/40 py-16 bg-[#111113]/40 border border-white/5 rounded-2xl">
            لا توجد كلانات مطابقة للبحث
          </div>
        )}
      </div>

      {/* مودال إنشاء كلان جديد */}
      {showCreate && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-200">
          <form
            onSubmit={create}
            data-testid="create-clan-form"
            className="bg-[#141417] border border-white/15 rounded-2xl p-6 w-full max-w-md space-y-5 shadow-2xl relative"
          >
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="absolute top-4 left-4 p-1.5 text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition"
            >
              <X size={18} />
            </button>

            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gold-500/10 border border-gold-500/30 text-gold-500 grid place-items-center">
                <Shield size={20} />
              </div>
              <div>
                <h2 className="font-display font-black text-xl text-white">إنشاء كلان جديد 🛡️</h2>
                <p className="text-xs text-white/50">أنشئ فريقك واجمع اللاعبين للمنافسة</p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-white/70 block mb-1">اسم الكلان</label>
                <input
                  data-testid="clan-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="مثال: Rivals Gaming"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold-500/60 text-sm text-white placeholder:text-white/30 transition"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-white/70 block mb-1">التاج (Tag)</label>
                <input
                  data-testid="clan-tag-input"
                  required
                  minLength={2}
                  maxLength={8}
                  value={form.tag}
                  onChange={(e) => setForm({ ...form, tag: e.target.value.toUpperCase() })}
                  placeholder="مثال: RVL (من 2 إلى 8 أحرف)"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold-500/60 text-sm text-white uppercase font-mono placeholder:text-white/30 transition"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-white/70 block mb-1">وصف الكلان (اختياري)</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="وصف مختصر عن اهتمامات وقوانين الكلان..."
                  rows={3}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold-500/60 resize-none text-sm text-white placeholder:text-white/30 transition"
                />
              </div>
            </div>

            <div className="flex gap-2.5 justify-end pt-2">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-4 py-2.5 rounded-xl hover:bg-white/5 text-xs text-white/70 font-bold transition"
                disabled={loadingCreate}
              >
                إلغاء
              </button>
              <button
                data-testid="submit-create-clan"
                type="submit"
                disabled={loadingCreate}
                className="px-5 py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 shadow-[0_0_15px_rgba(255,204,0,0.3)] disabled:opacity-60 text-xs transition"
              >
                {loadingCreate ? "جاري الإنشاء..." : "إنشاء الكلان"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
